import * as api from "@/api";
import { setValue } from "@/code-editor";
import PopupShell from "@/components/popup-shell";
import globals from "@/globals";
import * as signals from "@/signals";
import {
	getDomain,
	getFullDomain,
	getSuffix,
	loadSites,
	openSite,
} from "@/site";
import { closePopup, getPremium, showDialog, sleep } from "@/utils";
import { DialogTitle } from "@headlessui/react";
import { t } from "i18next";
import {
	ChangeEvent,
	FormEvent,
	JSX,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import {
	colors,
	NumberDictionary,
	uniqueNamesGenerator,
} from "unique-names-generator";

function AddSitePopup(): JSX.Element {
	const defaultDomain =
		signals.oldDomain.value ||
		uniqueNamesGenerator({
			dictionaries: [colors, NumberDictionary.generate()],
			separator: "",
			style: "lowerCase",
		});
	const defaultFullDomain = getFullDomain(defaultDomain);

	const [inputValue, setInputValue] = useState<string>(defaultFullDomain);
	const [isClosing, setIsClosing] = useState<boolean>(false);
	const [tipOverride, setTipOverride] = useState<string | null>(null);

	const inputId = useId();
	const inputRef = useRef<HTMLInputElement | null>(null);

	let isTutorialShown = false;
	let tip = t("defaultDomainTip", {
		suffix: getSuffix(),
	});

	const closeAddSitePopup = (): void => {
		setIsClosing(true);
		window.setTimeout(() => {
			closePopup("addSite");
			setIsClosing(false);
		}, globals.ANIMATION_WAIT_TIME);
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setInputValue(event.target.value);
		setTipOverride(null);
	};

	const handleSubmit = async (
		event: FormEvent<HTMLFormElement>,
	): Promise<void> => {
		event.preventDefault();
		if (!signals.login.value.username) {
			return;
		}
		let domain = getDomain(inputValue, defaultDomain);
		if (domain.includes(".")) {
			if (inputValue.endsWith(getSuffix())) {
				domain = domain.replaceAll(".", "-");
			} else if (signals.premiumRemaining.value <= 0) {
				domain = "";
				closeAddSitePopup();
				await showDialog(
					t("premiumRequiredForUsingCustomDomains") +
						t("refreshIfJustPaid"),
					{ showCancel: true },
				);
				void getPremium();
			}
		}
		if (!domain) {
			return;
		}
		const fullDomain = getFullDomain(domain);
		const newInputValue = domain.includes(".") ? domain : fullDomain;
		setInputValue(newInputValue);
		setTipOverride(t("registering", { domain: newInputValue }));
		try {
			const data = await api.createSite(
				domain,
				signals.oldDomain.value,
				async () => {
					closeAddSitePopup();
					await sleep(globals.ANIMATION_WAIT_TIME);
					await showDialog(
						t("premiumRequiredForCreatingMultipleSites") +
							t("refreshIfJustPaid"),
						{ showCancel: true },
					);
					void getPremium();
				},
			);
			if (data.success) {
				closeAddSitePopup();
				setValue();
				void loadSites();
				if (data.domain) {
					void openSite(getFullDomain(data.domain));
				}
			} else if (data.alert) {
				setTipOverride(t("error") + data.alert);
			}
		} catch (error) {
			setTipOverride(null);
			void api.handleApiError(error);
		}
		try {
			const data = await api.getBlockStatus();
			if (data.redirect) {
				window.location.href = data.redirect;
			}
		} catch (error) {
			void api.handleApiError(error);
		}
	};

	const domain = getDomain(inputValue, defaultDomain);
	if (
		domain.includes(".") &&
		!domain.startsWith(".") &&
		!inputValue.endsWith(getSuffix())
	) {
		tip =
			signals.premiumRemaining.value > 0
				? t("customDomainTip", {
						cname: domain + ".cname.rthe.cn",
					})
				: t("premiumRequiredForUsingCustomDomains") +
					t("refreshIfJustPaid");
		isTutorialShown = true;
	}

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	return (
		<PopupShell
			isClosing={isClosing}
			isOpen
			onClose={closeAddSitePopup}
		>
			<form onSubmit={(event) => void handleSubmit(event)}>
				<DialogTitle as="h1">
					<label htmlFor={inputId}>{t("setDomainName")}</label>
				</DialogTitle>
				<input
					className="input-box"
					id={inputId}
					onChange={handleInputChange}
					placeholder={defaultFullDomain}
					ref={inputRef}
					value={inputValue}
				/>
				<div className="tip">
					{tipOverride || tip}
					{isTutorialShown && (
						<a
							className="link"
							href="https://docs.retiehe.com/host/own-domain.html"
							target="_blank"
							rel="noreferrer"
						>
							{t("viewTutorial")}
						</a>
					)}
				</div>
				<div className="button-container">
					<button
						className="button"
						onClick={closeAddSitePopup}
						type="button"
					>
						{t("cancel")}
					</button>
					<button
						className="default-button"
						type="submit"
					>
						{t("ok")}
					</button>
				</div>
			</form>
		</PopupShell>
	);
}

export default AddSitePopup;
